import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { getQualifyingTotal } from '@theme/cart-free-gift';

const FIRST_SEGMENT_SHARE = 100 / 3;
const CHIP_END_THRESHOLD = 80;

/**
 * @typedef {{ type: 'shipping' | 'gift', threshold: number }} Milestone
 */

class FreeShippingTopbar extends HTMLElement {
  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartChange);
    document.addEventListener(ThemeEvents.discountUpdate, this.#handleCartChange);
    window.addEventListener('pageshow', this.#handlePageShow);

    this.#render(Number(this.dataset.cartTotal) || 0);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartChange);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#handleCartChange);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.#activeFetch?.abort();
  }

  #handlePageShow = (event) => {
    if (event.persisted) this.#fetchCart();
  };

  #handleCartChange = (event) => {
    const cart = event.detail?.resource;

    if (typeof cart?.total_price === 'number' && Array.isArray(cart.items)) {
      this.#render(getQualifyingTotal(cart));
      return;
    }

    this.#fetchCart();
  };

  async #fetchCart() {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(this.dataset.cartUrl || '/cart.js', {
        signal: this.#activeFetch.signal,
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();

      if (typeof cart.total_price === 'number') this.#render(getQualifyingTotal(cart));
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * @param {number} total
   */
  #render(total) {
    const milestones = this.#milestones;
    if (milestones.length === 0) return;

    const currentTotal = Math.max(total, 0);
    const next = milestones.find((milestone) => currentTotal < milestone.threshold);
    const progress = this.#getProgress(currentTotal, milestones);
    const remaining = next ? this.#formatMoney(next.threshold - currentTotal) : '';
    const valueText = next
      ? this.#fill(next.type === 'gift' ? this.dataset.textRemainingGift : this.dataset.textRemainingShipping, remaining)
      : this.dataset.textComplete || '';

    this.dataset.cartTotal = String(currentTotal);
    this.classList.toggle('is-complete', !next);
    this.style.setProperty('--mg-topbar-progress', String(progress / 100));

    this.querySelectorAll('[data-milestone-node]').forEach((node, index) => {
      node.classList.toggle('is-reached', currentTotal >= (milestones[index]?.threshold ?? Infinity));
    });

    this.querySelectorAll('[data-progress-remaining]').forEach((element) => {
      element.toggleAttribute('hidden', !next);
    });

    this.querySelectorAll('[data-progress-complete]').forEach((element) => {
      element.toggleAttribute('hidden', Boolean(next));
      element.textContent = next ? '' : valueText;
    });

    this.querySelectorAll('[data-progress-amount]').forEach((element) => {
      element.textContent = remaining;
    });

    const chip = this.querySelector('[data-progress-chip]');
    if (chip instanceof HTMLElement) {
      chip.hidden = !next;
      chip.textContent = next ? this.#fill(this.dataset.textChip, remaining) : '';
      chip.classList.toggle('mg-topbar__chip--end', progress > CHIP_END_THRESHOLD);
    }

    const progressBar = this.querySelector('[data-progress-bar]');
    if (progressBar) {
      progressBar.setAttribute('aria-valuenow', String(Math.round(progress)));
      progressBar.setAttribute('aria-valuetext', valueText);
    }
  }

  /**
   * Maps the cart total onto the Figma step grid: the first milestone sits at
   * one third of the bar, the second at the end.
   * @param {number} total
   * @param {Milestone[]} milestones
   * @returns {number}
   */
  #getProgress(total, milestones) {
    const [first, second] = milestones;

    if (!second) return Math.min((total / first.threshold) * 100, 100);
    if (total < first.threshold) return (total / first.threshold) * FIRST_SEGMENT_SHARE;

    const span = Math.max(second.threshold - first.threshold, 1);
    const secondShare = Math.min((total - first.threshold) / span, 1);

    return FIRST_SEGMENT_SHARE + secondShare * (100 - FIRST_SEGMENT_SHARE);
  }

  /** @returns {Milestone[]} */
  get #milestones() {
    try {
      const parsed = JSON.parse(this.dataset.milestones || '[]');
      return Array.isArray(parsed) ? parsed.filter((milestone) => milestone.threshold > 0) : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {string | undefined} template
   * @param {string} amount
   */
  #fill(template, amount) {
    return (template || '').replace('[amount]', amount);
  }

  /**
   * @param {number} amount
   * @returns {string}
   */
  #formatMoney(amount) {
    return formatMoney(amount, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'EUR');
  }
}

if (!customElements.get('free-shipping-topbar')) {
  customElements.define('free-shipping-topbar', FreeShippingTopbar);
}
